<template>
  <Dialog
    :header="!user.id ? 'Cadastro de Usuário' : 'Edição de usuário'"
    :visible="visible"
    :modal="true"
    :closable="false"
    @update:visible="$emit('update:visible', $event)"
    style="width: 500px"
  >
    <AppForm
      ref="form"
      :rules="rules"
      :values="user"
      v-slot:default="{ formErrors }"
    >
      <div class="p-fluid p-formgrid p-grid">
        <div class="p-field p-col-12 p-md-6">
          <AppFormItem :error="formErrors.name" label="Nome">
            <InputText v-model="user.name" />
          </AppFormItem>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <AppFormItem :error="formErrors.username" label="Username">
            <InputText v-model="user.username" />
          </AppFormItem>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <AppFormItem label="Email" :error="formErrors.email">
            <InputText v-model="user.email" />
          </AppFormItem>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <AppFormItem label="Telefone" :error="formErrors.phone">
            <InputText
              v-model="user.phone"
              v-mask="['(##) ####-####', '(##) #####-####']"
            />
          </AppFormItem>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <AppFormItem label="Tipo" :error="formErrors.roleId">
            <AppAutoComplete
              v-model="user.roleId"
              :options="roleOptions"
            ></AppAutoComplete>
          </AppFormItem>
        </div>

        <div
          v-if="$ability.can('manage', 'Admin')"
          class="p-field p-col-12 p-md-6"
        >
          <AppFormItem label="Conta" :error="formErrors.accountId">
            <AppAutoComplete
              v-model="user.accountId"
              :options="accountOptions"
              scrollHeight="100px"
            ></AppAutoComplete>
          </AppFormItem>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <AppFormItem :error="formErrors.password" label="Senha">
            <span class="p-input-icon-right">
              <InputText
                v-model="user.password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password"
              />
              <i
                :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
                @click="showPassword = !showPassword"
              />
            </span>
          </AppFormItem>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <AppFormItem
            label="Confirmar Senha"
            :error="formErrors.passwordConfirm"
          >
            <span class="p-input-icon-right">
              <InputText
                v-model="user.passwordConfirm"
                :type="showPassword ? 'text' : 'password'"
              />
              <i
                :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
                @click="showPassword = !showPassword"
              />
            </span>
          </AppFormItem>
        </div>
      </div>
    </AppForm>

    <template #footer>
      <div class="p-grid p-jc-between">
        <div class="p-col status-container">
          <span v-if="showStatus">
            <InputSwitch v-model="user.status"></InputSwitch>
            <p>Ativo</p>
          </span>
        </div>
        <div style="padding-right: 8px">
          <Button
            label="Cancelar"
            class="p-button-outlined p-mr-2"
            @click="cancel()"
          />
          <AppButton label="Salvar" @click="save()" :loading="loadingSave" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { AccountApi, UserApi as AdminUserApi } from '../../services/api/admin';
import { UserApi, UserTypeApi } from '../../services/api';
import { validateEmail } from '../../lib/helpers';

const defaultData = {
  id: null,
  name: '',
  username: '',
  email: '',
  phone: '',
  roleId: null,
  status: true,
  password: '',
  passwordConfirm: '',
  accountId: null,
};

export default {
  name: 'UserEdit',
  props: {
    visible: Boolean,
    showStatus: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      user: { ...defaultData },
      roleOptions: [
        { id: 1, name: 'Desenvolvedor' },
        { id: 2, name: 'Admin da Conta' },
      ],
      accountOptions: [],
      loadingSave: false,
      showPassword: false,
      rules: {
        name: [{ required: true, message: 'Informe o nome.' }],
        username: [{ required: true, message: 'Informe o username.' }],
        email: {
          validator(rule, value, callback) {
            if (!value) {
              callback(new Error('Informe um email.'));
            }
            if (validateEmail(value)) {
              return callback();
            }
            callback(new Error('Informe um email válido.'));
          },
        },
        phone: {
          validator(rule, value, callback) {
            value = value ? value.replace(/\D/g, '') : null;
            if (value && value.length < 10) {
              callback(new Error('Informe um telefone ou celular válido.'));
            }
            callback();
          },
        },
        roleId: {
          validator: (rule, value, callback) => {
            if (!value) {
              return callback(new Error('Selecione o tipo.'));
            }
            callback();
          },
        },
        accountId: {
          validator: (rule, value, callback) => {
            if (this.$ability.can('manage', 'Admin')) {
              if (!value && this.user.roleId && this.user.roleId !== 1) {
                return callback(new Error('Selecione a conta.'));
              }
            }
            callback();
          },
        },
        password: {
          validator: (rule, value, callback) => {
            if (!this.user.id && !value) {
              return callback(new Error('Informe a senha.'));
            }
            if (value && value.length < 6) {
              return callback(new Error('Insira no mínimo 6 caracteres.'));
            }
            callback();
          },
        },
        passwordConfirm: {
          validator: (rule, value, callback) => {
            if (this.user.password && !value) {
              return callback(new Error('Confirme a senha.'));
            }
            if (this.user.password && value && this.user.password !== value) {
              return callback(new Error('As senhas não coincidem.'));
            }
            callback();
          },
        },
      },
    };
  },
  async created() {
    if (this.$ability.can('manage', 'Admin')) {
      const { results: accounts } = await AccountApi.fetch({
        order: 'asc',
        orderField: 'name',
        pageSize: 50,
      });
      this.accountOptions = accounts;
      return;
    }

    const userTypeResponse = await UserTypeApi.fetch({
      order: 'asc',
      orderField: 'name',
      pageSize: 50,
    });
    this.roleOptions = userTypeResponse.results;
  },
  computed: {
    ...mapGetters('app', ['accountId']),
  },
  methods: {
    getApi() {
      if (this.$ability.can('manage', 'Admin')) {
        return AdminUserApi;
      }
      return UserApi;
    },
    setData(data) {
      if (!data) {
        return;
      }

      this.$nextTick(() => {
        this.accountOptions = [...this.accountOptions];
        this.roleOptions = [...this.roleOptions];
        this.reset();
        this.user = { ...data, password: '' };
        if (this.user.phone && this.user.phone.length === 10) {
          this.user.phone = this.$formatPhone(this.user.phone);
        }
        if (this.user.phone && this.user.phone.length === 11) {
          this.user.phone = this.$formatCellPhone(this.user.phone);
        }
      });
    },
    reset() {
      this.user = { ...defaultData };
    },
    cancel() {
      this.$emit('update:visible', false);
      this.reset();
    },
    async save() {
      try {
        if (!(await this.$refs.form.validate())) {
          return;
        }

        this.loadingSave = true;
        const body = {
          ...this.user,
        };

        if (!this.user.id) {
          await this.getApi().create(body);
        } else {
          await this.getApi().update(this.user.id, body);
        }

        this.$emit('update:visible', false);
        this.$emit('saved', body);
        this.reset();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingSave = false;
      }
    },
    handleError(error) {
      console.log(error);
      let detail = 'Não foi possível salvar o usuário.';

      if (error.code === 'AlreadyExistError') {
        detail = 'Já existe um cadastro com o mesmo "username".';
      }

      this.$toast.add({
        severity: 'error',
        summary: 'Desculpe! Não é possível salvar.',
        detail,
        life: 3000,
      });
    },
  },
};
</script>

<style scoped>
.status-container {
  position: relative;
  text-align: left;
}
.status-container p {
  margin: 0;
  position: absolute;
  top: 45%;
  transform: translate(0, -50%);
  margin-left: 50px;
}
</style>